import React from "react";
import styled from "styled-components";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from "recharts";

import { Flex } from "../../../../../components/Flex";
import { AtiraIcon } from "../../../../../components/AtiraIcon";
import { Info } from "../../../../../components/Info";
import { FontSize, Text } from "../../../../../components/Text";
import { Spacing } from "../../../../../theme/Spacing";
import { Breakpoints } from "../../../../../theme/Breakpoints";
import { ThemeType } from "../../../../../theme/Theme";
import { useTheme } from "../../../../../ThemeContext";
import { ReportWidget } from "../../ReportWidget";
import { ReportDelta } from "../../ReportDelta";
import {
  CHARTS_HEIGHT,
  renderPieChartCustomizedLabel,
} from "../../../../../utils/Reports";

const Title = styled(Text)`
  display: flex;
  align-items: center;
  gap: ${Spacing.s};
  font-weight: bold;
  font-size: ${FontSize.l};
  color: ${({ theme }) => theme.main};

  svg {
    width: 2.5rem;
  }
`;

const WidgetsWrapper = styled.div<{ columns: number }>`
  gap: ${Spacing.m};
  display: grid;
  grid-template-columns: 1fr;

  @media (min-width: ${Breakpoints.TABLET}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: ${Breakpoints.DESKTOP}) {
    grid-template-columns: repeat(${({ columns }) => columns}, 1fr);
  }
`;

const ChartFrame = styled(Flex)`
  width: 100%;
  max-width: 44rem;
  height: ${CHARTS_HEIGHT};
`;

export type SummaryMetric = {
  key: string;
  /** Widget heading. */
  title: string;
  /** Which window this number covers — the tiles do not share one. */
  subtitle?: string;
  value: number;
  /** The same figure over the previous window; omit for standing figures. */
  previousValue?: number;
  /** Up is the bad direction for this figure (deals lost, junk entries). */
  higherIsWorse?: boolean;
  color: keyof ThemeType;
  icon: IconProp;
  onView?: VoidFunction;
  /** This is the aggregate the other metrics in the section are slices of. */
  emphasize?: boolean;
};

type Props = {
  title: string;
  icon: IconProp;
  info: string;
  metrics: SummaryMetric[];
  /**
   * Draw a pie of the metrics. Only correct when they are parts of one whole.
   * Counts measured over different windows are left to the tiles alone: a
   * chart of three independent numbers just restates the three tiles below it.
   */
  pie?: boolean;
  columns?: number;
  /** Rendered between the chart and the tiles. */
  children?: React.ReactNode;
  /** Rendered below the tiles. */
  footer?: React.ReactNode;
};

export const ReportsSummarySection: React.FC<Props> = ({
  title,
  icon,
  info,
  metrics,
  pie,
  columns = 4,
  children,
  footer,
}) => {
  const { theme } = useTheme();

  const isDataEmpty = metrics.every(({ value }) => !value);
  const chartData = metrics.map((m) => ({
    name: m.title,
    value: m.value || 0,
    fill: theme[m.color] as string,
  }));

  return (
    <Flex flexDirection="column" gap="s">
      <Title>
        <AtiraIcon icon={icon} />
        {title}
      </Title>

      <Flex flexDirection="column" gap="l">
        <Flex flexDirection="column" gap="s">
          <Info info={info} />

          {/* No chart when there is nothing to plot. An empty-state placeholder
              here would just sit above tiles that already read 0. */}
          {pie && !isDataEmpty ? (
            <ChartFrame>
              <ResponsiveContainer>
                <PieChart>
                  <Tooltip wrapperStyle={{ zIndex: 999999 }} />
                  <Legend />
                  <Pie
                    cx="50%"
                    cy="50%"
                    dataKey="value"
                    data={chartData}
                    labelLine={false}
                    label={(props) =>
                      renderPieChartCustomizedLabel({ ...(props as any) })
                    }
                  >
                    {chartData.map((entry) => (
                      <Cell key={entry.name} fill={entry.fill} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </ChartFrame>
          ) : null}
        </Flex>

        {children}

        <WidgetsWrapper columns={columns}>
          {metrics.map((metric) => (
            <ReportWidget
              key={metric.key}
              title={metric.title}
              subtitle={metric.subtitle}
              icon={metric.icon}
              iconColor={metric.color}
              emphasize={metric.emphasize}
              badge={
                metric.previousValue === undefined ? null : (
                  <ReportDelta
                    current={metric.value ?? 0}
                    previous={metric.previousValue}
                    higherIsWorse={metric.higherIsWorse}
                  />
                )
              }
              onClick={metric.value > 0 ? metric.onView : undefined}
            >
              <Text fontSize={metric.emphasize ? "xxl" : "xl"}>
                {metric.value ?? 0}
              </Text>
            </ReportWidget>
          ))}
        </WidgetsWrapper>

        {footer}
      </Flex>
    </Flex>
  );
};
